import { useInfiniteQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { getChannelInvitesReceived } from '~/services/commissioner/commissioner-service.service.api';

import { ContestQueryKeys } from '../constants/query';
import type { ContestInvitesStatusFilter } from '../domains/services/enums/contest-invites-status-filter.enum';

const STALE_TIME = 30_000;
// TODO: Update for pagination once API is updated to support it
const PAGE_SIZE = 50;

type UseContestInvitesArgs = {
  status?: ContestInvitesStatusFilter;
  limit?: number;
  enabled?: boolean;
};

const useContestInvites = ({ status, limit = PAGE_SIZE, enabled }: UseContestInvitesArgs) => {
  const { user } = useContext(UserContext);
  const userId = user?.id;

  const data = useInfiniteQuery({
    queryKey: ContestQueryKeys.CONTEST_INVITES_RECEIVED({ userId: userId || '' }),
    staleTime: STALE_TIME,
    queryFn: ({ pageParam = 1 }) =>
      getChannelInvitesReceived({
        status: status as 'PENDING' | 'ACCEPTED' | undefined,
        limit,
        offset: (pageParam - 1) * limit,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.totalPages) {
        return undefined;
      }
      return lastPage.page + 1;
    },
    enabled: enabled && !!userId,
  });
  const itemsTotal = data.data?.pages?.[0]?.totalCount;

  const items = useMemo(() => data.data?.pages?.flatMap((page) => page.data) ?? [], [data]);

  return {
    ...data,
    itemsTotal,
    items,
  };
};

export default useContestInvites;
